import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { store } from './stores/store'
import { ADMIN_TAB_NAME, claimTabName, initWebCommon } from '@penn/web-common'
import { APP_CONTEXT, configureCognito } from './config/cognito'
import './assets/styles/globals.css'
import App from './App'

// Must precede anything that reads shared config. Mirror image of
// web-customer's values — this is the admin surface.
claimTabName(ADMIN_TAB_NAME)

initWebCommon({
  appContext: APP_CONTEXT,
  requiredGroup: 'admin',
  oppositeGroup: 'customer',
  wrongSurfaceMessage:
    'This account is not permitted to use the internal app. Please sign in via the customer portal.',
  // This surface keeps its in-app internal registration form.
  registerFallback: 'route',
})

configureCognito()

// BUG-LOW-aborted-xhr: benign cancelled/aborted XHRs — React Query cancelling an
// in-flight query on unmount, or a request the browser aborts on navigation —
// reject with an axios CanceledError / DOMException AbortError. Chromium ignores
// these; Firefox and WebKit surface them as uncaught "page errors" ("Request
// aborted" / "access control checks"). Swallow ONLY this cancellation class so
// genuine failures still propagate to the console and error boundaries.
const isBenignCancellation = (reason: unknown): boolean => {
  if (reason == null || typeof reason !== 'object') return false
  const r = reason as { name?: string; code?: string; message?: string }
  const message = String(r.message ?? '')
  return (
    r.name === 'CanceledError' || // axios v1 cancel
    r.name === 'AbortError' || // fetch / DOM abort
    r.code === 'ERR_CANCELED' || // axios cancel code
    /request aborted|the operation was aborted|the user aborted a request/i.test(
      message,
    )
  )
}
window.addEventListener('unhandledrejection', (event) => {
  if (isBenignCancellation(event.reason)) event.preventDefault()
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Every route/tab the admin opens re-hits the API on mount, so a page can
      // never show data another admin (or another tab) has already changed.
      // 'always' deliberately ignores each hook's `staleTime` — the cached rows
      // still render instantly (`isLoading` stays false), the refetch runs in
      // the background and only flips `isFetching`, so this is a freshness
      // guarantee, not a loading-spinner regression.
      refetchOnMount: 'always',
    },
    mutations: { retry: 0 },
  },
})

createRoot(root).render(
  <StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Provider>
  </StrictMode>,
)
